import React, { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import {
  IconArrowLeft,
  IconCornerDownRight,
  IconEye,
  IconEyeOff,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { arrayMoveImmutable } from 'array-move';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Container as DragAndDropContainer,
  Draggable,
  DropResult,
} from 'react-smooth-dnd';

import {
  Button,
  Icon,
  IconButton,
  Tag,
  TextField,
  TextFieldType,
  Typography,
} from '@cfa/react-components';

import { getNameFromLanguage } from '@/utils/language';
import { isApiError } from '@/utils/request';

import GenericError from '@/components/Error/GenericError';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import ConfirmationModal from '@/components/popups/ConfirmationModal';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants from '@/constants';

import {
  IntlCategoryDTO,
  IntlSubcategoryDTO,
  IntlSubtitleDTO,
} from '@/services/content-api-types';
import { LanguageObject } from '@/types/types';

import { getCategoryOptions } from '_generated/api-clients/xp';
import AddCategory, { Category } from '@/routes/Admin/components/AddCategory';
import AddIcon from '@/routes/Admin/components/AddIcon';
import AddProcedure from '@/routes/Admin/components/AddProcedure';
import ConfirmationModals from '@/routes/Admin/components/SubcategoryAccordion/ConfirmationModals';
import { SubcategoryAccordion } from '@/routes/Admin/components/SubcategoryAccordion/SubCategoryAccordion';
import { useEditCategory } from '@/routes/Admin/hooks';
import { setAdminState, useFocusAdminState } from '@/routes/Admin/state';

const EditCategory = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  if (!categoryId) {
    throw new Error('Missing param id');
  }

  const { t } = useTranslation();
  const { selectedCountry } = useFocusAdminState('selectedCountry');
  const { temporaryCategory, setTemporaryCategory } =
    useFocusAdminState('temporaryCategory');

  const isCanada =
    selectedCountry.label === Constants.ADMIN_MANAGING_COUNTRIES.CA.LABEL_KEY;
  const isUK =
    selectedCountry.label === Constants.ADMIN_MANAGING_COUNTRIES.GB.LABEL_KEY;
  const isSingapore =
    selectedCountry.label === Constants.ADMIN_MANAGING_COUNTRIES.SG.LABEL_KEY;

  const translationRequired = !isCanada && !isUK && !isSingapore;

  const [showAddIconPopUp, setShowAddIconPopUp] = useState(false);
  const [showAddSubcategory, setShowAddSubcategory] = useState(false);
  const [showCancelConfirmationPopUp, setShowCancelConfirmationPopUp] =
    useState(false);
  const [turnOffAllEditing, setTurnOffAllEditing] = useState(() => Date.now());
  const [backButtonPending, setBackButtonPending] = useState(false);
  const [
    enableCategoryButtonsFromChildren,
    setEnableCategoryButtonsFromChildren,
  ] = useState(false);

  const {
    data: categoryData,
    error,
    refetch: refetchCategoryData,
    isPending,
    isFetching,
  } = useQuery({
    ...getCategoryOptions({
      path: {
        id: categoryId,
      },
      query: { country: selectedCountry.label },
    }),
  }) as UseQueryResult<IntlCategoryDTO>;

  const { mutateAsync: editCategory, isPending: mutationPending } =
    useEditCategory();

  // Clear temporary category when switching to a different category to prevent stale data
  useEffect(() => {
    if (!(temporaryCategory.id && temporaryCategory.id !== categoryData?.id))
      return;
    setTemporaryCategory({
      id: '',
      displayIndex: 0,
      enabled: false,
      featured: false,
      name: { en: '', es: '' },
      icon: '',
      subcategories: [],
    });
  }, [categoryData?.id, setTemporaryCategory, temporaryCategory.id]);

  useEffect(() => {
    if (categoryData) {
      setTemporaryCategory(categoryData);
      setEnableCategoryButtonsFromChildren(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryData]);

  if (isApiError(error)) {
    datadogRum.addError(error);
    return <GenericError />;
  }

  const onNameChange = (e: React.ChangeEvent<TextFieldType>) => {
    // Guard against stale data - only update if temporary category matches current category
    if (temporaryCategory.id !== categoryData?.id) {
      return;
    }
    const name = e.target.value;
    if (name.length) {
      setTemporaryCategory({
        ...temporaryCategory,
        name: { en: name, es: temporaryCategory.name?.es ?? '' },
      });
    } else {
      setTemporaryCategory({
        ...temporaryCategory,
        name: { en: '', es: temporaryCategory.name?.es ?? '' },
      });
    }
  };

  const onTranslationChange = (e: React.ChangeEvent<TextFieldType>) => {
    const translation = e.target.value;
    // Guard against stale data - only update if temporary category matches current category
    if (temporaryCategory.id !== categoryData?.id) {
      return;
    }
    if (translation.length) {
      setTemporaryCategory({
        ...temporaryCategory,
        name: {
          en: temporaryCategory.name?.en ?? '',
          es: translation,
        },
      });
    } else {
      setTemporaryCategory({
        ...temporaryCategory,
        name: { en: temporaryCategory.name?.en ?? '', es: '' },
      });
    }
  };

  const onAddIcon = (icon: string) => {
    setTemporaryCategory({ ...temporaryCategory, icon });
    setShowAddIconPopUp(false);
  };

  const updateArrayIndex = (
    array: IntlSubcategoryDTO[] | undefined,
    removedIndex: number,
    addedIndex: number,
  ) => {
    if (removedIndex < 0 || addedIndex < 0 || !array) {
      return;
    }

    return arrayMoveImmutable(array, removedIndex, addedIndex).map(item => ({
      ...item,
    }));
  };

  const onDrop = (dropResult: DropResult) => {
    if (
      dropResult.removedIndex === null ||
      dropResult.removedIndex === dropResult.addedIndex ||
      dropResult.addedIndex === null
    ) {
      return;
    }

    const { removedIndex, addedIndex } = dropResult;

    const updatedSubcategories = updateArrayIndex(
      temporaryCategory.subcategories,
      removedIndex,
      addedIndex,
    );
    setTemporaryCategory({
      ...temporaryCategory,
      subcategories: updatedSubcategories ?? [],
    });
  };

  const onSaveSubtitlesOrder = (
    updatedSubtitles: IntlSubtitleDTO[],
    index: number,
  ) => {
    const copiedCategoryData = cloneDeep(temporaryCategory);
    copiedCategoryData.subcategories ??= [];
    const subcategory = copiedCategoryData.subcategories.at(index);
    if (subcategory?.subtitles) {
      subcategory.subtitles = updatedSubtitles;
    }
    setTemporaryCategory(copiedCategoryData);
  };

  const onSaveSubtitle = (
    pendingSectionMutation: { name: LanguageObject },
    index: number,
  ) => {
    const copiedCategoryData = cloneDeep(temporaryCategory);
    if (
      copiedCategoryData.subcategories &&
      !copiedCategoryData.subcategories[index]
    ) {
      copiedCategoryData.subcategories[index] = {};
    }

    if (copiedCategoryData.subcategories?.[index]?.subtitles) {
      copiedCategoryData.subcategories[index].subtitles = [
        ...(temporaryCategory.subcategories?.[index]?.subtitles ?? []),
        {
          documents: [],
          enabled: false,
          icon: '',
          name: {
            en: pendingSectionMutation.name.en ?? '',
            es: pendingSectionMutation.name.es ?? '',
          },
          visibleDocuments: [],
        },
      ];
    }
    setTemporaryCategory(copiedCategoryData);
  };

  const onCancelEdit = () => {
    if (!categoryData) return;
    setTemporaryCategory(categoryData);
    setShowCancelConfirmationPopUp(false);
    setAdminState('selectedSubcategoryId', undefined);
    setAdminState('selectedSubcategoryIndex', -1);
    setTurnOffAllEditing(Date.now());
    setEnableCategoryButtonsFromChildren(false);
    if (backButtonPending) {
      navigate(-1);
    }
  };

  const onShowToast = (name: string) => {
    toast.custom(toastObj => (
      <ToastMessageBlock id={toastObj.id}>
        {t('Admin.deleteSubcategoryToast', { subcategoryName: name.trim() })}
      </ToastMessageBlock>
    ));
  };

  const onAddSubcategory = (cat: Category) => {
    setShowAddSubcategory(false);
    setTemporaryCategory({
      ...temporaryCategory,
      subcategories: temporaryCategory.subcategories?.concat(
        // @ts-expect-error subcategories type mismatch with concat
        cat.subcategories,
      ),
    });
  };

  const onCancel = () => {
    setShowCancelConfirmationPopUp(true);
  };

  const onBackCancel = () => {
    setBackButtonPending(true);
    setShowCancelConfirmationPopUp(true);
  };

  const handleSaveCategory = async () => {
    const trimmedName = temporaryCategory.name?.en?.trim();
    const trimmedTranslation = temporaryCategory.name?.es?.trim();

    // Remove ids from visibleDocuments if they do not exist in the document array.
    const cleanSubcats = temporaryCategory.subcategories
      ? temporaryCategory.subcategories.map(subcat => {
          if (!subcat.subtitles) return subcat;
          const cleanSubtitles = subcat.subtitles.map(st => {
            if (!st.documents.length) {
              return st;
            }
            const docIds = new Set(st.documents.map(doc => doc.id));
            const cleanVisibleDocs = st.visibleDocuments
              ? [...docIds.intersection(new Set([...st.visibleDocuments]))]
              : [];
            return { ...st, visibleDocuments: cleanVisibleDocs };
          });
          return { ...subcat, subtitles: cleanSubtitles };
        })
      : [];

    const payload: IntlCategoryDTO = {
      country: selectedCountry.label,
      displayIndex: temporaryCategory.displayIndex,
      enabled: temporaryCategory.enabled,
      featured: temporaryCategory.featured ? true : false,
      icon: temporaryCategory.icon,
      id: categoryId,
      locationTypes: temporaryCategory.locationTypes,
      name: {
        en: trimmedName ?? '',
        es: trimmedTranslation ?? '',
      },
      subcategories: cleanSubcats,
    };

    await editCategory({ payload, categoryName: trimmedName });
    await refetchCategoryData();
    setTurnOffAllEditing(Date.now());
    setEnableCategoryButtonsFromChildren(false);
  };

  const isCurrent = temporaryCategory.id === categoryData?.id;
  return (
    <>
      <LoadingOverlay isOpen={isPending} />
      {!isPending && categoryData && (
        <div className="centered-content px-4">
          <div className="flex items-center pt-9 pr-0 pb-7">
            <IconArrowLeft
              className="text-grayscale-gray6! mr-3! cursor-pointer!"
              data-testid="EditCategoryBackButton"
              onClick={
                isEqual(temporaryCategory, categoryData)
                  ? () => navigate(-1)
                  : onBackCancel
              }
              size={24}
            />

            <Typography className="text-24/12! font-bold! capitalize!">
              {`${t('Generic.edit')} ${
                temporaryCategory.name
                  ? getNameFromLanguage(temporaryCategory.name)
                  : ''
              }`}
            </Typography>
            {temporaryCategory.enabled ? (
              <Tag
                className="ml-[1em]! flex-row-reverse! items-center! [&_.tag-leading-element]:m-[0_0_0_6px]! [&_.tag-leading-element]:size-[unset]!"
                data-testid="EditCategoryToggleShow"
                label={t('Admin.categoryIsVisible')}
                leadingElement={<IconEye width={10} />}
                onClick={() =>
                  setAdminState('confirmationModalType', 'categoryVisibility')
                }
                variant="filled"
              />
            ) : (
              <button
                type="button"
                className="cursor-pointer"
                onClick={() =>
                  setAdminState('confirmationModalType', 'categoryVisibility')
                }
              >
                <Tag
                  className="ml-[1em]! flex-row-reverse! items-center! [&_.tag-leading-element]:m-[0_0_0_6px]! [&_.tag-leading-element]:size-[unset]!"
                  data-testid="EditCategoryToggleHide"
                  disabled
                  label={t('Admin.categoryHidden')}
                  leadingElement={<IconEyeOff width={10} />}
                  variant="filled"
                />
              </button>
            )}
            <IconButton
              className="ml-auto"
              aria-label="Delete Category"
              color="default"
              data-testid="EditCategoryTrashIcon"
              href=""
              onClick={() =>
                setAdminState('confirmationModalType', 'categoryDelete')
              }
              size="md"
            >
              <IconTrash />
            </IconButton>
          </div>
          <div className="flex w-full justify-center gap-4">
            <div className="relative flex flex-col items-center">
              <Typography
                className="leading-6 font-medium text-nowrap"
                fontWeight="medium"
                variant="body2"
              >
                {t('Admin.editIcon')}
              </Typography>
              <button
                type="button"
                className="cursor-pointer"
                onClick={() => setShowAddIconPopUp(true)}
              >
                <img
                  className="w-10"
                  alt="Category Icon"
                  src={
                    temporaryCategory.icon
                      ? `${Constants.PATHWAY_CDN_IMG.CATEGORIES}${temporaryCategory.icon}.svg`
                      : `${Constants.PATHWAY_CDN_IMG.CATEGORIES}Chickfila.svg`
                  }
                />
              </button>
              {showAddIconPopUp && (
                <AddIcon
                  className="absolute top-[60px] left-2.5 z-5 translate-[unset]! transform-[unset]! [&>div:first-of-type]:transform-[unset]!"
                  onAddIcon={icon => onAddIcon(icon)}
                  onClose={() => setShowAddIconPopUp(false)}
                />
              )}
            </div>

            <div className="flex w-full flex-col">
              <TextField
                data-testid={'EditCategoryNameInput'}
                key={`name-${categoryData.id}`}
                disabled={!isCurrent}
                fullWidth
                label={
                  temporaryCategory.featured
                    ? t('Admin.recommendedCategory')
                    : t('Admin.category')
                }
                onChange={onNameChange}
                placeholder={'Enter Name'}
                required
                spellCheck="false"
                value={isCurrent ? temporaryCategory.name?.en : ''}
              />

              <div className="flex w-full flex-row">
                <IconCornerDownRight size={35} />
                <div className="flex w-full flex-col">
                  <div>
                    <TextField
                      data-testid={'EditCategoryTranslationInput'}
                      fullWidth
                      label={t('Admin.spanishTranslation')}
                      key={`translation-${categoryData.id}`}
                      onChange={onTranslationChange}
                      placeholder={'Enter Translation'}
                      required={translationRequired}
                      spellCheck="false"
                      disabled={!isCurrent}
                      value={isCurrent ? temporaryCategory.name?.es : ''}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {temporaryCategory.subcategories?.length ? (
            <>
              <Typography className="text-brand-dark-gray text-16/6 pt-8 pr-0 pb-2 font-bold uppercase">
                {t('Admin.subcategories')}
              </Typography>
              <div className="[&_.shadow-on-drop]:bg-brand-dark-gray pathway-smooth-dnd-container w-full [&_.shadow-on-drop]:rounded-[5px] [&_.shadow-on-drop]:p-2.5">
                {/** @ts-expect-error it's yelling about children but we need children for the drag and drop */}
                <DragAndDropContainer
                  dragHandleSelector=".subcategory-drag-handle"
                  dropPlaceholder={{
                    className: 'shadow-on-drop',
                  }}
                  onDrop={e => onDrop(e)}
                >
                  {temporaryCategory.subcategories.map(
                    (subcategory, subcategoryIndex) => (
                      // @ts-expect-error it's yelling about children but we need children for the drag and drop
                      <Draggable key={`${subcategoryIndex}-${subcategory.id}`}>
                        <div className="inline-block w-full no-underline">
                          {
                            <SubcategoryAccordion
                              adminCategoryId={categoryId}
                              category={categoryData}
                              subcategory={subcategory}
                              countryCode={selectedCountry.label}
                              index={subcategoryIndex}
                              isCanada={isCanada}
                              isUK={isUK}
                              onSaveSubtitle={onSaveSubtitle}
                              onSaveSubtitlesOrder={onSaveSubtitlesOrder}
                              showAdminFunctionality={true}
                              turnOffAllEditing={turnOffAllEditing}
                              setEnableCategoryButtonsFromChildren={
                                setEnableCategoryButtonsFromChildren
                              }
                              translationRequired={translationRequired}
                            />
                          }
                        </div>
                      </Draggable>
                    ),
                  )}
                </DragAndDropContainer>
              </div>
            </>
          ) : null}

          <div className="mt-6">
            {!showAddSubcategory && (
              <Button
                color="secondary"
                onClick={() => setShowAddSubcategory(prev => !prev)}
                variant="text"
              >
                <Icon
                  className="text-brand-navy mr-2 cursor-pointer"
                  icon={IconPlus}
                  size="sm"
                />
                <Typography color="secondary" data-testid="AddSubcategory">
                  {t('Admin.addSubcategory')}
                </Typography>
              </Button>
            )}
          </div>
          {showAddSubcategory && categoryData.icon && (
            <AddCategory
              categoryToEdit={{
                icon: categoryData.icon,
                id: categoryId,
                name: {
                  en: categoryData.name?.en ?? '',
                  es: categoryData.name?.es ?? '',
                },
              }}
              isSubcategory={true}
              onSubcategoryAdd={onAddSubcategory}
              onSubcategoryCancel={() => setShowAddSubcategory(false)}
              translationRequired={translationRequired}
            />
          )}

          <div className="mt-12 flex justify-center gap-4">
            <Button
              color="secondary"
              data-testid="EditCategoryCancelButton"
              disabled={
                (isEqual(temporaryCategory, categoryData) &&
                  !enableCategoryButtonsFromChildren) ||
                isFetching ||
                mutationPending
              }
              onClick={onCancel}
              variant="outlined"
            >
              {t('Button.cancel')}
            </Button>
            <Button
              color="secondary"
              data-testid="EditCategorySaveButton"
              disabled={
                (isEqual(temporaryCategory, categoryData) &&
                  !enableCategoryButtonsFromChildren) ||
                isFetching ||
                mutationPending
              }
              onClick={handleSaveCategory}
              variant="filled"
            >
              {t('Button.save')}
            </Button>
          </div>
        </div>
      )}

      <AddProcedure />
      <ConfirmationModal
        bodyText={t('Admin.cancelEditCategoryModalBody')}
        headerText={t('Admin.warning')}
        isOpen={showCancelConfirmationPopUp}
        onClose={() => setShowCancelConfirmationPopUp(false)}
        primaryButtonHandler={onCancelEdit}
        primaryButtonText={t('Button.cancelChanges')}
        secondaryButtonHandler={prev => setShowCancelConfirmationPopUp(!prev)}
        secondaryButtonText={t('Button.cancel')}
        primaryButtonColor="primary"
        primaryButtonVariant="destructive"
      />
      {categoryData && (
        <ConfirmationModals
          category={categoryData}
          countryCode={selectedCountry.label}
          onShowToast={(name: string) => onShowToast(name)}
        />
      )}
    </>
  );
};

export default EditCategory;
